import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, KeyRound, ShieldCheck } from 'lucide-react';
import { z } from 'zod';
import { schemas } from '@shared/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormActions, FormField } from '@/components/inputs/Form';
import { PasswordStrengthMeter } from '@/components/inputs/PasswordStrengthMeter';
import { PASSWORD_MIN_LENGTH } from '@shared/index';
import { ErrorState } from '@/components/states';
import { callApi } from '@/lib/apiClient';
import { paths } from '@/routes/paths';
import { AuthCard } from './AuthLayout';
import { authErrorMessage, useAuthActions, useAuthState } from './useAuth';

function Banner({ tone, children }: { tone: 'error' | 'success'; children: React.ReactNode }) {
  const danger = tone === 'error';
  return (
    <div
      role={danger ? 'alert' : 'status'}
      className={`mb-4 flex items-start gap-2 rounded-md border p-3 text-caption ${
        danger
          ? 'border-danger-fill bg-danger-bg text-danger'
          : 'border-success-fill bg-success-bg text-success'
      }`}
    >
      {danger ? (
        <AlertCircle aria-hidden className="mt-px size-4 shrink-0" />
      ) : (
        <CheckCircle2 aria-hidden className="mt-px size-4 shrink-0" />
      )}
      {children}
    </div>
  );
}

/** A new password plus confirmation, used by activation, reset and change. */
const newPasswordFields = z
  .object({
    password: schemas.identity.activateBody.shape.password,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Both passwords must match',
    path: ['confirmPassword'],
  });

type NewPasswordValues = z.infer<typeof newPasswordFields>;

// ── Forgot password ─────────────────────────────────────────────────────────────

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const form = useForm<{ email: string }>({
    resolver: zodResolver(schemas.identity.forgotPasswordBody),
    defaultValues: { email: '' },
  });

  if (sent) {
    return (
      <AuthCard
        title="Check your email"
        footer={
          <Link to={paths.login} className="text-link hover:underline">
            Back to sign in
          </Link>
        }
      >
        {/* Deliberately does NOT say whether the address exists. The server behaves the
            same way, and a UI that distinguishes them would undo that. */}
        <p className="text-body text-fg-muted">
          If an account exists for that address, we have sent a link to reset the
          password. The link expires in one hour.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      description="We will email you a link to set a new one."
      footer={
        <Link to={paths.login} className="text-link hover:underline">
          Back to sign in
        </Link>
      }
    >
      <Form
        form={form}
        onSubmit={async (values) => {
          // This endpoint always succeeds, so there is nothing to branch on.
          await callApi('auth.forgotPassword', { body: values });
          setSent(true);
        }}
      >
        <FormField name="email" label="Work email" required>
          {(field) => (
            <Input {...field} {...form.register('email')} type="email" autoComplete="username" autoFocus />
          )}
        </FormField>
        <FormActions className="sm:justify-stretch">
          <Button type="submit" variant="primary" className="h-10 w-full" loading={form.formState.isSubmitting}>
            Send reset link
          </Button>
        </FormActions>
      </Form>
    </AuthCard>
  );
}

// ── Set a password from a token (activation and reset) ──────────────────────────

function TokenPasswordPage({
  mode,
}: {
  mode: 'activate' | 'reset';
}) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { activate, resetPassword } = useAuthActions();
  const [formError, setFormError] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const form = useForm<NewPasswordValues>({
    resolver: zodResolver(newPasswordFields),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const newPasswordValue = form.watch('password');

  // Validate the token BEFORE showing the form, so an expired link says so immediately
  // rather than after the user has chosen and typed a password twice.
  useEffect(() => {
    if (mode !== 'activate' || !token) return;
    let cancelled = false;

    void callApi('auth.activationInfo', { query: { token } })
      .then((res) => {
        if (!cancelled) setAccountEmail(res.data.email);
      })
      .catch(() => {
        if (!cancelled) setTokenInvalid(true);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, token]);

  if (!token || tokenInvalid) {
    return (
      <AuthCard title={mode === 'activate' ? 'Activate your account' : 'Set a new password'}>
        <ErrorState
          title="This link is no longer valid"
          description="Activation and reset links expire. Ask your administrator for a new invitation, or request another reset."
        />
        <div className="text-center text-caption">
          <Link to={paths.forgotPassword} className="text-link hover:underline">
            Request a new link
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={mode === 'activate' ? 'Activate your account' : 'Set a new password'}
      description={
        accountEmail
          ? `Choose a password for ${accountEmail}.`
          : `Choose a password of at least ${PASSWORD_MIN_LENGTH} characters.`
      }
    >
      {formError && <Banner tone="error">{formError}</Banner>}

      <Form
        form={form}
        onSubmit={async (values) => {
          setFormError(null);
          try {
            if (mode === 'activate') await activate(token, values.password);
            else await resetPassword(token, values.password);
            navigate(paths.dashboard, { replace: true });
          } catch (err) {
            setFormError(authErrorMessage(err));
          }
        }}
      >
        <FormField
          name="password"
          label="New password"
          required
          helper={`At least ${PASSWORD_MIN_LENGTH} characters.`}
        >
          {(field) => (
            <>
              <Input {...field} {...form.register('password')} type="password" autoComplete="new-password" autoFocus />
              <PasswordStrengthMeter value={newPasswordValue} className="mt-2" />
            </>
          )}
        </FormField>

        <FormField name="confirmPassword" label="Confirm password" required>
          {(field) => (
            <Input {...field} {...form.register('confirmPassword')} type="password" autoComplete="new-password" />
          )}
        </FormField>

        <FormActions className="sm:justify-stretch">
          <Button type="submit" variant="primary" className="h-10 w-full" loading={form.formState.isSubmitting}>
            {mode === 'activate' ? 'Activate account' : 'Set password'}
          </Button>
        </FormActions>
      </Form>
    </AuthCard>
  );
}

export const ActivatePage = () => <TokenPasswordPage mode="activate" />;
export const ResetPasswordPage = () => <TokenPasswordPage mode="reset" />;

// ── Change password (signed in) ─────────────────────────────────────────────────

const changeFields = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    password: schemas.identity.changePasswordBody.shape.newPassword,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Both passwords must match',
    path: ['confirmPassword'],
  });

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const { reloadSession, logout } = useAuthActions();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm<z.infer<typeof changeFields>>({
    resolver: zodResolver(changeFields),
    defaultValues: { currentPassword: '', password: '', confirmPassword: '' },
  });
  const newPasswordValue = form.watch('password');

  const forced = user?.mustChangePassword === true;
  const displayName = user?.employee?.fullName ?? user?.email ?? 'Your account';

  return (
    <AuthCard
      title={forced ? 'Set a new password' : 'Change your password'}
      description={
        forced
          ? 'Your administrator has asked you to set a new password before continuing.'
          : 'Other devices will be signed out when you save a new password.'
      }
      footer={
        forced ? (
          <button
            type="button"
            className="text-link hover:underline"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        ) : (
          <Link to={paths.dashboard} className="text-link hover:underline">
            Back to BM-Work
          </Link>
        )
      }
    >
      {forced && (
        <div className="mb-5 space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-action/20 bg-action/5 px-3 py-2 text-caption font-medium text-fg">
            <ShieldCheck aria-hidden className="size-4 shrink-0 text-action" />
            One step before you can use BM-Work
          </div>
          <div className="flex items-start gap-3 rounded-md border border-line bg-subtle px-4 py-3">
            <div
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface text-fg-muted shadow-xs"
            >
              <KeyRound className="size-4" />
            </div>
            <div className="min-w-0 space-y-0.5 text-left">
              <p className="truncate text-body font-medium text-fg">{displayName}</p>
              <p className="truncate text-caption text-fg-muted">{user?.email}</p>
              <p className="pt-1 text-caption text-fg-muted">
                Use the password you just signed in with as{' '}
                <span className="font-medium text-fg">Current password</span>, then choose
                something only you will know.
              </p>
            </div>
          </div>
        </div>
      )}

      {formError && <Banner tone="error">{formError}</Banner>}
      {done && !forced && <Banner tone="success">Your password has been changed.</Banner>}

      <Form
        form={form}
        onSubmit={async (values) => {
          setFormError(null);
          try {
            await callApi('auth.changePassword', {
              body: { currentPassword: values.currentPassword, newPassword: values.password },
            });
            setDone(true);
            form.reset();
            const me = await reloadSession();
            if (!me.mustChangePassword) navigate(paths.dashboard, { replace: true });
          } catch (err) {
            setFormError(authErrorMessage(err));
          }
        }}
      >
        <FormField
          name="currentPassword"
          label={forced ? 'Current password (from sign-in)' : 'Current password'}
          required
        >
          {(field) => (
            <Input
              {...field}
              {...form.register('currentPassword')}
              type="password"
              autoComplete="current-password"
              autoFocus
            />
          )}
        </FormField>

        <FormField
          name="password"
          label="New password"
          required
          helper={`At least ${PASSWORD_MIN_LENGTH} characters.`}
        >
          {(field) => (
            <>
              <Input {...field} {...form.register('password')} type="password" autoComplete="new-password" />
              <PasswordStrengthMeter value={newPasswordValue} className="mt-2" />
            </>
          )}
        </FormField>

        <FormField name="confirmPassword" label="Confirm new password" required>
          {(field) => (
            <Input {...field} {...form.register('confirmPassword')} type="password" autoComplete="new-password" />
          )}
        </FormField>

        <FormActions className="sm:justify-stretch">
          <Button type="submit" variant="primary" className="h-10 w-full" loading={form.formState.isSubmitting}>
            {forced ? 'Save and continue' : 'Change password'}
          </Button>
        </FormActions>
      </Form>
    </AuthCard>
  );
}
